import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';

import { Card, Page } from '../../app/layout/Page';
import {
  displayUnitLabel,
  useUnitDisplay,
} from '../../app/units/UnitDisplayContext';
import type {
  DisplayUnit,
  QuantityKind,
} from '../../app/units/UnitDisplayContext';
import { queryKeys } from '../../app/query/queryClient';
import { catalogApi } from './api';
import type { CatalogApi } from './api';
import {
  HOSE_FIELD_ORDER,
  HOSE_KEY_MAX_LENGTH,
  HOSE_STRING_MAX_LENGTH,
  hoseErrorFromUnknown,
  suggestHoseKey,
  validateHoseDraft,
} from './hoseValidation';
import type { HoseErrorState, HoseFieldName } from './hoseValidation';
import type {
  HoseTypeInput,
  HoseTypeRead,
  PumpRevisionInput,
  PumpTemplateCreateInput,
} from './types';

import './catalog.css';
import { guided } from '../../app/guidance/fieldHelpRegistryCore';
import { catalogFieldHelp } from '../../app/guidance/fieldHelpRegistry';

interface CatalogWorkspaceProps {
  api?: CatalogApi;
}

interface PumpFormState {
  dischargeDiameterM: string;
  manufacturer: string;
  model: string;
  revisionLabel: string;
  suctionDiameterM: string;
  templateName: string;
  variantName: string;
}

interface HoseFormState {
  availableCount: string;
  insideDiameterM: string;
  key: string;
  model: string;
  name: string;
  roughnessM: string;
  standardLength: DisplayValueDraft;
  workingPressure: DisplayValueDraft;
}

interface DisplayValueDraft {
  unit: DisplayUnit;
  value: string;
}

function HoseFieldError({
  error,
  field,
}: {
  error: string | undefined;
  field: HoseFieldName;
}) {
  return error === undefined ? null : (
    <span className="equipment-field-error" id={`hose-${field}-error`}>
      {error}
    </span>
  );
}

const emptyPumpForm: PumpFormState = {
  dischargeDiameterM: '0.3048',
  manufacturer: '',
  model: '',
  revisionLabel: 'A',
  suctionDiameterM: '0.3048',
  templateName: '',
  variantName: 'Standard trim',
};

const DEFAULT_HOSE_LENGTH_M = 201.168;
const DEFAULT_HOSE_PRESSURE_PA = 2_068_427.188;

type UnitConverter = (
  value: number,
  quantity: QuantityKind,
  unit?: DisplayUnit,
) => number;

function humanRoundedNumber(value: number): string {
  return Number(value.toFixed(3)).toString();
}

function displayDraftFromSi(
  value: number,
  quantity: QuantityKind,
  unit: DisplayUnit,
  toDisplayValue: UnitConverter,
): DisplayValueDraft {
  return {
    unit,
    value: humanRoundedNumber(toDisplayValue(value, quantity, unit)),
  };
}

function createEmptyHoseForm(
  lengthUnit: DisplayUnit,
  pressureUnit: DisplayUnit,
  toDisplayValue: UnitConverter,
): HoseFormState {
  return {
    availableCount: '0',
    insideDiameterM: '0.3048',
    key: '',
    model: '',
    name: '',
    roughnessM: '0.00015',
    standardLength: displayDraftFromSi(
      DEFAULT_HOSE_LENGTH_M,
      'head',
      lengthUnit,
      toDisplayValue,
    ),
    workingPressure: displayDraftFromSi(
      DEFAULT_HOSE_PRESSURE_PA,
      'pressure',
      pressureUnit,
      toDisplayValue,
    ),
  };
}

function userProvenance() {
  return {
    captured_at: new Date().toISOString(),
    source_kind: 'user_entry' as const,
    source_name: 'Flow Wizard catalog form',
  };
}

function pumpPayload(
  form: PumpFormState,
  source: PumpRevisionInput | null,
): PumpTemplateCreateInput {
  return {
    template_name: form.templateName,
    revision: {
      confidence: source?.confidence ?? 'low',
      discharge_diameter_m: Number(form.dischargeDiameterM),
      expected_maximum_gear_ratio:
        source?.expected_maximum_gear_ratio ?? null,
      expected_minimum_gear_ratio:
        source?.expected_minimum_gear_ratio ?? null,
      impeller_trim_notes: source?.impeller_trim_notes ?? null,
      manufacturer: form.manufacturer,
      maximum_suction_lift_m: source?.maximum_suction_lift_m ?? null,
      minimum_suction_pressure_absolute_pa:
        source?.minimum_suction_pressure_absolute_pa ?? null,
      model: form.model,
      notes: source?.notes ?? null,
      provenance: source?.provenance ?? userProvenance(),
      pump_type: source?.pump_type ?? 'centrifugal',
      revision_label: form.revisionLabel,
      suction_diameter_m: Number(form.suctionDiameterM),
      variants: source
        ? source.variants.map((variant) => ({
            curve_links: variant.curve_links.map((link) => ({
              curve_name: link.curve_name,
              curve_revision: link.curve_revision,
              curve_set_id: link.curve_set_id,
              optimization_eligible: link.optimization_eligible,
              verification_state: link.verification_state,
            })),
            impeller_diameter_m: variant.impeller_diameter_m,
            name: variant.name,
            trim_description: variant.trim_description,
          }))
        : [
            {
              curve_links: [],
              impeller_diameter_m: null,
              name: form.variantName,
              trim_description: null,
            },
          ],
      vendor_maximum_power_w: source?.vendor_maximum_power_w ?? null,
      vendor_maximum_pressure_gauge_pa:
        source?.vendor_maximum_pressure_gauge_pa ?? null,
      vendor_maximum_pump_speed_rpm:
        source?.vendor_maximum_pump_speed_rpm ?? null,
      vendor_minimum_pump_speed_rpm:
        source?.vendor_minimum_pump_speed_rpm ?? null,
      verification_state: source?.verification_state ?? 'draft',
    },
  };
}

function hosePayload(
  form: HoseFormState,
  source: HoseTypeRead | null,
  toSiValue: UnitConverter,
): HoseTypeInput {
  return {
    available_count: Number(form.availableCount),
    darcy_weisbach_roughness_m: Number(form.roughnessM),
    hazen_williams_c: source?.hazen_williams_c ?? null,
    inside_diameter_m: Number(form.insideDiameterM),
    key: form.key.trim(),
    manufacturer: source?.manufacturer ?? null,
    model: form.model.trim(),
    name: form.name.trim(),
    nominal_diameter_m:
      source?.nominal_diameter_m ?? Number(form.insideDiameterM),
    provenance: source?.provenance ?? userProvenance(),
    standard_fittings: source?.standard_fittings ?? [],
    standard_length_m: toSiValue(
      Number(form.standardLength.value),
      'head',
      form.standardLength.unit,
    ),
    supported_friction_methods:
      source?.supported_friction_methods ?? ['darcy_weisbach'],
    working_pressure_gauge_pa: toSiValue(
      Number(form.workingPressure.value),
      'pressure',
      form.workingPressure.unit,
    ),
  };
}

function conciseNumber(value: number): string {
  return Number(value.toPrecision(10)).toString();
}

function visibleDraftValue(
  draft: DisplayValueDraft,
  quantity: QuantityKind,
  currentUnit: DisplayUnit,
  toDisplayValue: UnitConverter,
  toSiValue: UnitConverter,
): string {
  if (draft.unit === currentUnit || draft.value.trim() === '') return draft.value;
  const parsed = Number(draft.value);
  if (!Number.isFinite(parsed)) return draft.value;
  const siValue = toSiValue(parsed, quantity, draft.unit);
  return humanRoundedNumber(toDisplayValue(siValue, quantity, currentUnit));
}

export function CatalogWorkspace({ api = catalogApi }: CatalogWorkspaceProps) {
  const { formatSiValue, toDisplayValue, toSiValue, units } = useUnitDisplay();
  const displayInput = (
    value: string,
    quantity: 'diameter' | 'head' | 'pressure',
  ) => {
    if (value.trim() === '') return '';
    const parsed = Number(value);
    return Number.isFinite(parsed)
      ? conciseNumber(toDisplayValue(parsed, quantity))
      : value;
  };
  const canonicalInput = (
    value: string,
    quantity: 'diameter' | 'head' | 'pressure',
  ) => {
    if (value.trim() === '') return '';
    const parsed = Number(value);
    return Number.isFinite(parsed)
      ? conciseNumber(toSiValue(parsed, quantity))
      : value;
  };
  const queryClient = useQueryClient();
  const [view, setView] = useState<'pumps' | 'hoses'>('pumps');
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [pumpForm, setPumpForm] = useState(emptyPumpForm);
  const [editingPumpId, setEditingPumpId] = useState<string | null>(null);
  const [editingRevision, setEditingRevision] = useState<PumpRevisionInput | null>(null);
  const [hoseForm, setHoseForm] = useState(() =>
    createEmptyHoseForm(units.head, units.pressure, toDisplayValue),
  );
  const [editingHose, setEditingHose] = useState<HoseTypeRead | null>(null);
  const [hoseErrors, setHoseErrors] = useState<HoseErrorState | null>(null);
  const [hoseKeyIsManual, setHoseKeyIsManual] = useState(false);
  const [roughnessEstimateId, setRoughnessEstimateId] = useState(
    'new-smooth-plastic-liner',
  );
  const [csvText, setCsvText] = useState('');
  const [notice, setNotice] = useState('');
  const hoseFieldRefs = useRef<
    Partial<Record<HoseFieldName, HTMLInputElement | null>>
  >({});
  const hoseErrorSummaryRef = useRef<HTMLDivElement | null>(null);

  const presentHoseErrors = (errors: HoseErrorState) => {
    setHoseErrors(errors);
    const firstInvalidField = HOSE_FIELD_ORDER.find(
      (field) => errors.fieldErrors[field] !== undefined,
    );
    if (firstInvalidField !== undefined) {
      hoseFieldRefs.current[firstInvalidField]?.focus();
      return;
    }
    window.setTimeout(() => hoseErrorSummaryRef.current?.focus(), 0);
  };

  const clearHoseErrors = () => setHoseErrors(null);

  const hoseErrorId = (field: HoseFieldName) =>
    hoseErrors?.fieldErrors[field] === undefined
      ? undefined
      : `hose-${field}-error`;

  const hoseDescribedBy = (
    field: HoseFieldName,
    ...descriptionIds: Array<string | undefined>
  ) => {
    const ids = [...descriptionIds, hoseErrorId(field)].filter(
      (value): value is string => value !== undefined,
    );
    return ids.length === 0 ? undefined : ids.join(' ');
  };

  const archived = showArchived ? 'include' : 'exclude';
  const pumpKey = queryKeys.resource('pump-templates');
  const hoseKey = queryKeys.resource('hose-types');
  const pumps = useQuery({
    enabled: view === 'pumps',
    queryFn: () => api.listPumps(search, archived),
    queryKey: pumpKey.list({ archived, search }),
  });
  const hoses = useQuery({
    enabled: view === 'hoses',
    queryFn: () => api.listHoses(search, archived),
    queryKey: hoseKey.list({ archived, search }),
  });
  const hoseGuidance = useQuery({
    queryFn: () => api.getHoseGuidance(),
    queryKey: [...hoseKey.all, 'roughness-guidance'],
  });

  const refreshPumps = () => queryClient.invalidateQueries({ queryKey: pumpKey.all });
  const refreshHoses = () => queryClient.invalidateQueries({ queryKey: hoseKey.all });
  const savePump = useMutation({
    mutationFn: (payload: PumpTemplateCreateInput) =>
      editingPumpId === null
        ? api.createPump(payload)
        : api.revisePump(editingPumpId, payload),
    onSuccess: async () => {
      setNotice(editingPumpId === null ? 'Pump model added.' : 'Pump revision saved.');
      setEditingPumpId(null);
      setEditingRevision(null);
      setPumpForm(emptyPumpForm);
      await refreshPumps();
    },
  });
  const setPumpArchive = useMutation({
    mutationFn: ({ archived: isArchived, id }: { archived: boolean; id: string }) =>
      isArchived ? api.restorePump(id) : api.archivePump(id),
    onSuccess: refreshPumps,
  });
  const saveHose = useMutation({
    mutationFn: (payload: HoseTypeInput) =>
      editingHose === null
        ? api.createHose(payload)
        : api.updateHose(editingHose.id, payload),
    onSuccess: async () => {
      setNotice(editingHose === null ? 'Hose added.' : 'Hose saved.');
      setEditingHose(null);
      setHoseErrors(null);
      setHoseKeyIsManual(false);
      setHoseForm(
        createEmptyHoseForm(units.head, units.pressure, toDisplayValue),
      );
      await refreshHoses();
    },
    onError: (error) => presentHoseErrors(hoseErrorFromUnknown(error)),
  });
  const setHoseArchive = useMutation({
    mutationFn: ({ archived: isArchived, id }: { archived: boolean; id: string }) =>
      isArchived ? api.restoreHose(id) : api.archiveHose(id),
    onSuccess: refreshHoses,
  });
  const importHoses = useMutation({
    mutationFn: () => api.importHoses(csvText, false),
    onSuccess: async (result) => {
      setNotice(
        result.invalid_rows === 0
          ? `${result.imported_count} hoses imported.`
          : `No hoses imported. Fix ${result.invalid_rows} rows.`,
      );
      await refreshHoses();
    },
  });

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setSearch(searchDraft);
  };
  const submitPump = (event: FormEvent) => {
    event.preventDefault();
    savePump.mutate(pumpPayload(pumpForm, editingRevision));
  };
  const submitHose = (event: FormEvent) => {
    event.preventDefault();
    setNotice('');
    const errors = validateHoseDraft({
      availableCount: hoseForm.availableCount,
      insideDiameterM: hoseForm.insideDiameterM,
      key: hoseForm.key,
      model: hoseForm.model,
      name: hoseForm.name,
      roughnessM: hoseForm.roughnessM,
      standardLength: hoseForm.standardLength.value,
      workingPressure: hoseForm.workingPressure.value,
    });
    if (errors !== null) {
      presentHoseErrors(errors);
      return;
    }

    const payload = hosePayload(hoseForm, editingHose, toSiValue);
    const convertedValues: Array<[HoseFieldName, number]> = [
      ['insideDiameterM', payload.inside_diameter_m],
      ['roughnessM', payload.darcy_weisbach_roughness_m ?? Number.NaN],
      ['standardLength', payload.standard_length_m],
      ['workingPressure', payload.working_pressure_gauge_pa],
    ];
    const nonFiniteField = convertedValues.find(([, value]) => !Number.isFinite(value));
    if (nonFiniteField !== undefined) {
      const field = nonFiniteField[0];
      presentHoseErrors({
        correlationId: null,
        fieldErrors: {
          [field]: 'The converted SI value is outside the supported numeric range.',
        },
        summaryMessages: [
          'The converted SI value is outside the supported numeric range.',
        ],
      });
      return;
    }

    setHoseErrors(null);
    saveHose.mutate(payload);
  };
  const editPump = async (id: string) => {
    const pump = await api.getPump(id);
    const revision: PumpRevisionInput = pump.current_revision;
    setEditingPumpId(pump.id);
    setEditingRevision(revision);
    setPumpForm({
      dischargeDiameterM: String(revision.discharge_diameter_m),
      manufacturer: revision.manufacturer,
      model: revision.model,
      revisionLabel: `${revision.revision_label}-next`,
      suctionDiameterM: String(revision.suction_diameter_m),
      templateName: pump.template_name,
      variantName: revision.variants[0]?.name ?? 'Standard trim',
    });
  };
  const editHose = (hose: HoseTypeRead) => {
    setEditingHose(hose);
    setHoseErrors(null);
    setHoseKeyIsManual(true);
    setHoseForm({
      availableCount: String(hose.available_count),
      insideDiameterM: String(hose.inside_diameter_m),
      key: hose.key,
      model: hose.model,
      name: hose.name,
      roughnessM: String(hose.darcy_weisbach_roughness_m ?? ''),
      standardLength: displayDraftFromSi(
        hose.standard_length_m,
        'head',
        units.head,
        toDisplayValue,
      ),
      workingPressure: displayDraftFromSi(
        hose.working_pressure_gauge_pa,
        'pressure',
        units.pressure,
        toDisplayValue,
      ),
    });
  };
  const selectedRoughnessEstimate =
    hoseGuidance.data?.entries.find(
      (estimate) => estimate.key === roughnessEstimateId,
    ) ?? hoseGuidance.data?.entries[0];

  return (
    <Page title="Catalog">
      <div className="equipment-toolbar">
        <div role="tablist" aria-label="Catalog record type">
          <button type="button" role="tab" aria-selected={view === 'pumps'} onClick={() => setView('pumps')}>
            Pump models
          </button>
          <button type="button" role="tab" aria-selected={view === 'hoses'} onClick={() => setView('hoses')}>
            Hoses
          </button>
        </div>
        <form role="search" onSubmit={submitSearch}>
          <label>
            Search catalog
            <input {...guided(catalogFieldHelp.search)} value={searchDraft} onChange={(event) => setSearchDraft(event.target.value)} />
          </label>
          <button type="submit">Search</button>
        </form>
        <label>
          <input {...guided(catalogFieldHelp.showArchived)} type="checkbox" checked={showArchived} onChange={(event) => setShowArchived(event.target.checked)} />
          Show archived
        </label>
      </div>
      {notice ? <p role="status">{notice}</p> : null}

      {view === 'pumps' ? (
        <div className="equipment-grid">
          <Card title={editingPumpId === null ? 'Add pump model' : 'New pump revision'}>
            <form className="equipment-form" onSubmit={submitPump}>
              <label>Pump name<input {...guided(catalogFieldHelp.templateName)} required value={pumpForm.templateName} onChange={(event) => setPumpForm({ ...pumpForm, templateName: event.target.value })} /></label>
              <label>Manufacturer<input {...guided(catalogFieldHelp.manufacturer)} required value={pumpForm.manufacturer} onChange={(event) => setPumpForm({ ...pumpForm, manufacturer: event.target.value })} /></label>
              <label>Model<input {...guided(catalogFieldHelp.model)} required value={pumpForm.model} onChange={(event) => setPumpForm({ ...pumpForm, model: event.target.value })} /></label>
              <label>Revision label<input {...guided(catalogFieldHelp.revisionLabel)} required value={pumpForm.revisionLabel} onChange={(event) => setPumpForm({ ...pumpForm, revisionLabel: event.target.value })} /></label>
              <label>Suction diameter ({displayUnitLabel(units.diameter)})<input {...guided(catalogFieldHelp.suctionDiameter)} required type="number" min="0" step="any" value={displayInput(pumpForm.suctionDiameterM, 'diameter')} onChange={(event) => setPumpForm({ ...pumpForm, suctionDiameterM: canonicalInput(event.target.value, 'diameter') })} /></label>
              <label>Discharge diameter ({displayUnitLabel(units.diameter)})<input {...guided(catalogFieldHelp.dischargeDiameter)} required type="number" min="0" step="any" value={displayInput(pumpForm.dischargeDiameterM, 'diameter')} onChange={(event) => setPumpForm({ ...pumpForm, dischargeDiameterM: canonicalInput(event.target.value, 'diameter') })} /></label>
              <label>Variant name<input {...guided(catalogFieldHelp.variantName)} required value={pumpForm.variantName} onChange={(event) => setPumpForm({ ...pumpForm, variantName: event.target.value })} /></label>
              <button type="submit" disabled={savePump.isPending}>{editingPumpId === null ? 'Add pump model' : 'Save revision'}</button>
            </form>
          </Card>
          <Card title={`Saved pump models (${pumps.data?.total ?? 0})`}>
            {pumps.isLoading ? <p>Loading pumps…</p> : null}
            <ul className="equipment-list">
              {pumps.data?.items.map((pump) => (
                <li key={pump.id}>
                  <div><strong>{pump.template_name}</strong><span>{pump.manufacturer} {pump.model} · Revision {pump.revision_label}</span><span>{pump.has_optimization_eligible_curve ? 'Curve ready' : 'Curve needed'}</span></div>
                  <div><button type="button" onClick={() => void editPump(pump.id)}>Edit</button><button type="button" onClick={() => setPumpArchive.mutate({ archived: pump.archived, id: pump.id })}>{pump.archived ? 'Restore' : 'Archive'}</button></div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      ) : (
        <div className="equipment-grid">
          <div>
            <Card title={editingHose === null ? 'Add hose' : 'Edit hose'}>
              <form className="equipment-form" noValidate onSubmit={submitHose}>
                {hoseErrors === null ? null : (
                  <div
                    aria-labelledby="hose-error-summary-title"
                    className="equipment-error-summary"
                    ref={hoseErrorSummaryRef}
                    role="alert"
                    tabIndex={-1}
                  >
                    <h3 id="hose-error-summary-title">
                      Fix these fields
                    </h3>
                    <ul>
                      {hoseErrors.summaryMessages.map((message, index) => (
                        <li key={`${index}-${message}`}>{message}</li>
                      ))}
                    </ul>
                    {hoseErrors.correlationId === null ? null : (
                      <p>
                        Support reference: <code>{hoseErrors.correlationId}</code>
                      </p>
                    )}
                  </div>
                )}
                <label>
                  Name
                  <input {...guided(catalogFieldHelp.hoseName)}
                    aria-label="Name"
                    aria-describedby={hoseDescribedBy('name')}
                    aria-invalid={hoseErrors?.fieldErrors.name === undefined ? undefined : 'true'}
                    maxLength={HOSE_STRING_MAX_LENGTH}
                    ref={(element) => {
                      hoseFieldRefs.current.name = element;
                    }}
                    required
                    value={hoseForm.name}
                    onChange={(event) => {
                      const name = event.target.value;
                      clearHoseErrors();
                      setHoseForm((current) => ({
                        ...current,
                        key: hoseKeyIsManual ? current.key : suggestHoseKey(name),
                        name,
                      }));
                    }}
                  />
                  <HoseFieldError error={hoseErrors?.fieldErrors.name} field="name" />
                </label>
                <label>
                  Hose key
                  <input {...guided(catalogFieldHelp.hoseKey)}
                    aria-label="Hose key"
                    aria-describedby={hoseDescribedBy('key', 'hose-key-hint')}
                    aria-invalid={hoseErrors?.fieldErrors.key === undefined ? undefined : 'true'}
                    maxLength={HOSE_KEY_MAX_LENGTH}
                    pattern="[A-Za-z0-9][A-Za-z0-9._-]*"
                    ref={(element) => {
                      hoseFieldRefs.current.key = element;
                    }}
                    required
                    value={hoseForm.key}
                    onChange={(event) => {
                      clearHoseErrors();
                      setHoseKeyIsManual(true);
                      setHoseForm({ ...hoseForm, key: event.target.value });
                    }}
                  />
                  <span className="equipment-field-hint" id="hose-key-hint">
                    Suggested from the name. You can edit it.
                  </span>
                  <HoseFieldError error={hoseErrors?.fieldErrors.key} field="key" />
                </label>
                <label>
                  Model
                  <input {...guided(catalogFieldHelp.hoseModel)}
                    aria-label="Model"
                    aria-describedby={hoseDescribedBy('model')}
                    aria-invalid={hoseErrors?.fieldErrors.model === undefined ? undefined : 'true'}
                    maxLength={HOSE_STRING_MAX_LENGTH}
                    ref={(element) => {
                      hoseFieldRefs.current.model = element;
                    }}
                    required
                    value={hoseForm.model}
                    onChange={(event) => {
                      clearHoseErrors();
                      setHoseForm({ ...hoseForm, model: event.target.value });
                    }}
                  />
                  <HoseFieldError error={hoseErrors?.fieldErrors.model} field="model" />
                </label>
                <label>
                  Inside diameter ({displayUnitLabel(units.diameter)})
                  <input {...guided(catalogFieldHelp.hoseInsideDiameter)}
                    aria-label={`Inside diameter (${displayUnitLabel(units.diameter)})`}
                    aria-describedby={hoseDescribedBy('insideDiameterM')}
                    aria-invalid={hoseErrors?.fieldErrors.insideDiameterM === undefined ? undefined : 'true'}
                    ref={(element) => {
                      hoseFieldRefs.current.insideDiameterM = element;
                    }}
                    required
                    step="any"
                    type="number"
                    value={displayInput(hoseForm.insideDiameterM, 'diameter')}
                    onChange={(event) => {
                      clearHoseErrors();
                      setHoseForm({
                        ...hoseForm,
                        insideDiameterM: canonicalInput(event.target.value, 'diameter'),
                      });
                    }}
                  />
                  <HoseFieldError
                    error={hoseErrors?.fieldErrors.insideDiameterM}
                    field="insideDiameterM"
                  />
                </label>
                <label>
                  Standard length ({displayUnitLabel(units.head)})
                  <input {...guided(catalogFieldHelp.hoseStandardLength)}
                    aria-label={`Standard length (${displayUnitLabel(units.head)})`}
                    aria-describedby={hoseDescribedBy('standardLength')}
                    aria-invalid={hoseErrors?.fieldErrors.standardLength === undefined ? undefined : 'true'}
                    ref={(element) => {
                      hoseFieldRefs.current.standardLength = element;
                    }}
                    required
                    step="any"
                    type="number"
                    value={visibleDraftValue(
                      hoseForm.standardLength,
                      'head',
                      units.head,
                      toDisplayValue,
                      toSiValue,
                    )}
                    onChange={(event) => {
                      clearHoseErrors();
                      setHoseForm({
                        ...hoseForm,
                        standardLength: { unit: units.head, value: event.target.value },
                      });
                    }}
                  />
                  <HoseFieldError
                    error={hoseErrors?.fieldErrors.standardLength}
                    field="standardLength"
                  />
                </label>
                <label>
                  Available count
                  <input {...guided(catalogFieldHelp.hoseAvailableCount)}
                    aria-label="Available count"
                    aria-describedby={hoseDescribedBy('availableCount')}
                    aria-invalid={hoseErrors?.fieldErrors.availableCount === undefined ? undefined : 'true'}
                    min="0"
                    ref={(element) => {
                      hoseFieldRefs.current.availableCount = element;
                    }}
                    required
                    step="1"
                    type="number"
                    value={hoseForm.availableCount}
                    onChange={(event) => {
                      clearHoseErrors();
                      setHoseForm({ ...hoseForm, availableCount: event.target.value });
                    }}
                  />
                  <HoseFieldError
                    error={hoseErrors?.fieldErrors.availableCount}
                    field="availableCount"
                  />
                </label>
                <label>
                  Working pressure (gauge {displayUnitLabel(units.pressure)})
                  <input {...guided(catalogFieldHelp.hoseWorkingPressure)}
                    aria-label={`Working pressure (gauge ${displayUnitLabel(units.pressure)})`}
                    aria-describedby={hoseDescribedBy('workingPressure')}
                    aria-invalid={hoseErrors?.fieldErrors.workingPressure === undefined ? undefined : 'true'}
                    ref={(element) => {
                      hoseFieldRefs.current.workingPressure = element;
                    }}
                    required
                    step="any"
                    type="number"
                    value={visibleDraftValue(
                      hoseForm.workingPressure,
                      'pressure',
                      units.pressure,
                      toDisplayValue,
                      toSiValue,
                    )}
                    onChange={(event) => {
                      clearHoseErrors();
                      setHoseForm({
                        ...hoseForm,
                        workingPressure: {
                          unit: units.pressure,
                          value: event.target.value,
                        },
                      });
                    }}
                  />
                  <HoseFieldError
                    error={hoseErrors?.fieldErrors.workingPressure}
                    field="workingPressure"
                  />
                </label>
                <label>
                  Darcy roughness ({displayUnitLabel(units.diameter)})
                  <input {...guided(catalogFieldHelp.hoseRoughness)}
                    aria-label={`Darcy roughness (${displayUnitLabel(units.diameter)})`}
                    aria-describedby={hoseDescribedBy(
                      'roughnessM',
                      'darcy-roughness-confirmation',
                    )}
                    aria-invalid={hoseErrors?.fieldErrors.roughnessM === undefined ? undefined : 'true'}
                    min="0"
                    ref={(element) => {
                      hoseFieldRefs.current.roughnessM = element;
                    }}
                    required
                    step="any"
                    type="number"
                    value={displayInput(hoseForm.roughnessM, 'diameter')}
                    onChange={(event) => {
                      clearHoseErrors();
                      setHoseForm({
                        ...hoseForm,
                        roughnessM: canonicalInput(event.target.value, 'diameter'),
                      });
                    }}
                  />
                  <HoseFieldError
                    error={hoseErrors?.fieldErrors.roughnessM}
                    field="roughnessM"
                  />
                </label>
                <details className="roughness-helper">
                  <summary>Roughness helper</summary>
                  <div className="roughness-helper__body">
                  {hoseGuidance.isLoading ? <p role="status">Loading sourced hose guidance…</p> : null}
                  {hoseGuidance.isError ? <p role="alert">Sourced hose guidance could not be loaded. Enter a reviewed value manually.</p> : null}
                  {selectedRoughnessEstimate === undefined ? null : (
                    <>
                      <label>
                        Closest hose family
                        <select {...guided(catalogFieldHelp.roughnessEstimate)} value={selectedRoughnessEstimate.key} onChange={(event) => setRoughnessEstimateId(event.target.value)}>
                          {hoseGuidance.data?.entries.map((estimate) => (
                        <option key={estimate.key} value={estimate.key}>
                          {estimate.label} · {formatSiValue(estimate.darcy_weisbach_roughness_m, 'diameter', { maximumFractionDigits: 6 })}
                        </option>
                          ))}
                        </select>
                      </label>
                      <p>{selectedRoughnessEstimate.liner_material}. {selectedRoughnessEstimate.evidence_basis}</p>
                      <div className="roughness-helper__actions">
                        <button
                          type="button"
                          onClick={() => {
                            clearHoseErrors();
                            setHoseForm((current) => ({
                              ...current,
                              roughnessM: String(
                                selectedRoughnessEstimate.darcy_weisbach_roughness_m,
                              ),
                            }));
                          }}
                        >
                          Use this estimate
                        </button>
                        {selectedRoughnessEstimate.sources.map((source) => (
                          <a href={source.url} key={source.url} target="_blank" rel="noreferrer">{source.title}</a>
                        ))}
                      </div>
                      <p id="darcy-roughness-confirmation" className="roughness-helper__caution">
                        General estimate only. {selectedRoughnessEstimate.cautions.join(' ')}
                      </p>
                    </>
                  )}
                  </div>
                </details>
                <button type="submit" disabled={saveHose.isPending}>{editingHose === null ? 'Add hose' : 'Save hose'}</button>
              </form>
            </Card>
            <details className="card equipment-disclosure">
              <summary>Import hose CSV</summary>
              <div className="equipment-disclosure__body">
                <label>CSV content<textarea {...guided(catalogFieldHelp.csv)} rows={5} value={csvText} onChange={(event) => setCsvText(event.target.value)} /></label>
                <button type="button" disabled={!csvText || importHoses.isPending} onClick={() => importHoses.mutate()}>Import CSV</button>
              </div>
            </details>
          </div>
          <Card title={`Saved hoses (${hoses.data?.total ?? 0})`}>
            {hoses.isLoading ? <p>Loading hoses…</p> : null}
            <ul className="equipment-list">
              {hoses.data?.items.map((hose) => (
                <li key={hose.id}>
                  <div><strong>{hose.key} · {hose.name}</strong><span>{hose.model} · {formatSiValue(hose.inside_diameter_m, 'diameter')} ID · {formatSiValue(hose.working_pressure_gauge_pa, 'pressure')} working</span><span>{hose.available_count} × {formatSiValue(hose.standard_length_m, 'head')} available</span></div>
                  <div><button type="button" onClick={() => editHose(hose)}>Edit</button><button type="button" onClick={() => setHoseArchive.mutate({ archived: hose.archived, id: hose.id })}>{hose.archived ? 'Restore' : 'Archive'}</button></div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}
    </Page>
  );
}
